import { makeStyles, useTheme } from '@mui/styles';
import { Container, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as OTPAuth from 'otpauth';

import useAppContext from '../common/hooks/useAppContext';
import AppForm from '../common/components/AppForm';

const useStyles = makeStyles(theme => ({
  background: {
    height: "100vh",
    background: "rgb(248, 250, 251)",
    overflow: "auto"
  },
  space6: {
    marginTop: theme.spacing(6),
  },
  loginContainer: {
    marginLeft: "auto !important",
    marginRight: "auto !important",
    background: "#fff",
    padding: "24px 150px",
    borderRadius: 8
  },
  loginTitle: {
    fontSize: "24px !important",
    fontWeight: "700 !important",
    marginBottom: "8px !important"
  },
  loginSubtitle: {
    fontSize: "14px !important",
    color: "#666 !important",
    marginBottom: "24px !important"
  },
}));

const passwordSchema = yup.string()
  .required('新しいパスワードを入力してください')
  .min(8, 'パスワードは8文字以上で入力してください');

const confirmPasswordSchema = yup.string()
  .required('確認用パスワードを入力してください')
  .test('passwords-match', 'パスワードが一致しません', function (value) {
    return this.parent.newPassword === value;
  });

const formFields = [
  {
    name: 'newPassword',
    label: '新しいパスワード',
    type: 'password',
    required: true,
    schema: passwordSchema,
  },
  {
    name: 'confirmPassword',
    label: '新しいパスワード（確認）',
    type: 'password',
    required: true,
    schema: confirmPasswordSchema,
  },
];

const FirstLoginPage = () => {
  const classes = useStyles();
  const app = useAppContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const handleSubmit = async (values) => {
    const loginId = localStorage.getItem('loginId');
    const session = localStorage.getItem('firstLoginSession');

    if (!loginId || !session) {
      navigate('/login');
      return;
    }

    try {
      app.setIsLoading(true);
      const res = await app.http().post('/auth/first-login-password', {
        loginId,
        newPassword: values.newPassword,
        session,
      });

      // セッション情報をクリア
      localStorage.removeItem('firstLoginSession');

      if (res.SecretCode) {
        // MFA_SETUP: MFA設定画面へ
        let totp = new OTPAuth.TOTP({
          issuer: 'Kajima',
          label: 'Kajima',
          secret: res.SecretCode,
        });
        let uri = totp.toString();
        totp = OTPAuth.URI.parse(uri);
        localStorage.setItem('qrcodeStr', totp);
        localStorage.setItem('SecretCode', res.SecretCode);
        localStorage.setItem('Session', res.Session);
        localStorage.setItem('password', values.newPassword);
        navigate('/setup-mfa');
      } else if (res.ChallengeName) {
        // その他チャレンジ（SOFTWARE_TOKEN_MFA等）
        localStorage.setItem('password', values.newPassword);
        navigate('/verify-code');
      } else if (res.AuthenticationResult) {
        // 認証完了: トークン保存 → 通常画面へ
        localStorage.clear();
        localStorage.setItem('access_token', res.AuthenticationResult.AccessToken);
        localStorage.setItem('refresh_token', res.AuthenticationResult.RefreshToken);
        const user = await app.http().get('/user');
        app.login(user);
        navigate(app.pathAfterLogin);
      }
    } catch (err) {
      app.handleHttpError(err);
    } finally {
      app.setIsLoading(false);
    }
  };

  const handleCancel = () => {
    localStorage.removeItem('firstLoginSession');
    localStorage.removeItem('loginId');
    navigate('/login');
  };

  const submitActions = [
    { name: '変更して続行', handler: handleSubmit, fullWidth: true },
    { name: 'キャンセル', handler: handleCancel, fullWidth: true, variant: 'outlined', color: 'inherit', skipValidation: true },
  ];

  return (
    <div className={classes.background}>
      <div className={classes.space6} />
      <Container component="main" maxWidth="sm" className={classes.loginContainer}>
        <div style={smUp ? {padding: "24px 100px"} : {padding: "24px 0"}}>
          <Typography align="center" className={classes.loginTitle}>
            新しいパスワードの設定
          </Typography>
          <Typography align="center" className={classes.loginSubtitle}>
            初回ログインのため、新しいパスワードを設定してください。
          </Typography>
          <AppForm formFields={formFields} submitActions={submitActions} columns={{ xs: 1, md: 1 }} />
        </div>
      </Container>
    </div>
  );
};

export default FirstLoginPage;
